// ** Amplify
import { API } from "aws-amplify"
// **  store names
import { STORES } from "@constants/redux"

// ** main app Logger
import { appLogger } from "@helpers/logger"

import { globalConst } from "@constants/global"

// **  utility
import { isEmpty, orderBy, drop } from "lodash"

// ** fuzzy search support
import Fuse from "fuse.js"
import { devLog } from "@helpers/devLog"

const shouldSkipProtectedDashboardCalls = () => {
  // Server-authoritative admin dashboard: disable local fallback mode.
  return false
}

/** initla state for auth store  */
const initialState = {
  data: [],
  current: {},
  perPageValues: [5, 10, 25, 50],
  itemPerPage: 5,
  currentPage: 1,
  filters: {},
  error: null,
  message: "",
  keyword: null,
  action: globalConst.action.NONE
}

export const notifications = {
  state: {
    ...initialState,
    deliveryLog: [],
    activeTab: "campaigns"
  },

  reducers: {
    /**
     * Set translate message
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    setMessage: (s, payload) => {
      return {
        ...s,
        message: payload
      }
    },
    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    setData: (s, payload) => {
      return {
        ...s,
        data: payload
      }
    },
    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    pushNewData: (s, payload) => {
      const can = Array.isArray(s.data) ? s.data : []
      return {
        ...s,
        data: [...can, payload]
      }
    },
    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    setFilters: (s, payload) => {
      return {
        ...s,
        filters: { ...s.filters, ...payload }
      }
    },
    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    setKeyword: (s, payload) => {
      return {
        ...s,
        keyword: payload
      }
    },
    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    setitemPerPage: (s, payload) => {
      return {
        ...s,
        itemPerPage: payload
      }
    },
    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    setCurrent: (s, payload) => {
      return {
        ...s,
        current: payload
      }
    },
    setDeliveryLog: (s, payload) => {
      return {
        ...s,
        deliveryLog: payload
      }
    },
    setActiveTab: (s, payload) => {
      return {
        ...s,
        activeTab: payload
      }
    },

    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    setCurrentPage: (s, payload) => {
      return {
        ...s,
        currentPage: payload
      }
    },
    setError: (s, payload) => {
      return {
        ...s,
        error: payload
      }
    },

    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    setAction: (s, payload) => {
      return {
        ...s,
        action: payload
      }
    }
  },
  selectors: (slice, createSelector) => ({
    /**
     *
     * @returns
     */
    error() {
      return createSelector(
        [slice, (rootState) => rootState.loading.models.notifications],
        (notifications, notificationsLoading) => {
          return notificationsLoading.error || ""
        }
      )
    },

    /**
     * success selector used in different
     * auth actions
     *
     * @returns {String}
     */
    success() {
      return createSelector(
        [slice, (rootState) => rootState.loading.models.notifications],
        (notifications, notificationsLoading) => notificationsLoading.success
      )
    },
    paginationData() {
      return createSelector([slice], (notifications) => {
        let _data = notifications.data
        
        // Apply filters if they exist
        if (!isEmpty(notifications.filters)) {
          const status = notifications.filters.status
          _data = !!status
            ? status.value !== "all"
              ? _data.filter((e) => e.status === status.value.toUpperCase())
              : _data
            : _data
        }
        
        // Apply keyword search if it exists
        if (!!notifications.keyword) {
          const options = {
            keys: ["title", "body", "category"]
          }
          const fuse = new Fuse(_data, options)
          let data = fuse.search(notifications.keyword)
          data = data.map((e) => e.item)
          _data = data
        }
        
        // Always apply pagination
        const currentPage = notifications.currentPage || 1
        const offset = (currentPage - 1) * notifications.itemPerPage
        _data = drop(_data, offset).slice(0, notifications.itemPerPage)
        
        return orderBy(_data, (a) => new Date(a.updatedAt), ["desc"])
      })
    }
  }),
  effects: (dispatch) => ({
    /**
     *
     */
    async fetchDeliveryLog(filters = {}) {
      appLogger.info("fetchDeliveryLog", `[REDUX][${STORES.NOTIFICATIONS}]`)
      this.setAction(globalConst.action.FETCH_NOTIFICATIONS)
      try {
        const params = new URLSearchParams()
        if (filters.auctionId) params.set("auctionId", filters.auctionId)
        if (filters.userId) params.set("userId", filters.userId)
        params.set("limit", String(filters.limit || 100))
        const qs = params.toString()
        const result = await API.get(
          "prefsApiGateway",
          `/notifications/delivery-log${qs ? `?${qs}` : ""}`
        )
        this.setDeliveryLog(result?.items || [])
        this.setAction(globalConst.action.NONE)
        return result?.items || []
      } catch (error) {
        this.setDeliveryLog([])
        this.setAction(globalConst.action.NONE)
        return []
      }
    },
    async fetchNotifications() {
      appLogger.info("fetchNotifications", `[REDUX][${STORES.NOTIFICATIONS}]`)
      this.setAction(globalConst.action.FETCH_NOTIFICATIONS)
      if (shouldSkipProtectedDashboardCalls()) {
        this.setData([])
        this.setAction(globalConst.action.NONE)
        return
      }
      try {
        const result = await API.get("prefsApiGateway", "/notifications")

        this.setData(result)
        this.setAction(globalConst.action.NONE)
      } catch (error) {
        this.setData([])
        this.setAction(globalConst.action.NONE)
        return
      }
    },
    /**
     *
     * @param {*} payload
     */
    async sendNotification(payload) {
      appLogger.info("sendNotification", `[REDUX][${STORES.NOTIFICATIONS}]`)
      this.setAction(globalConst.action.SEND_NOTIFICATIONS)
      try {
        if (shouldSkipProtectedDashboardCalls()) {
          this.setMessage("msg_notf_sent_sucess")
          this.setAction(globalConst.action.NONE)
          return
        }
        const result = await API.post("prefsApiGateway", "/notifications", {
          body: payload
        })

        this.setMessage("msg_notf_sent_sucess")
        this.setAction(globalConst.action.NONE)
      } catch (error) {
        this.setAction(globalConst.action.NONE)
        return
      }
    },

    /**
     *
     * @param {*} id
     */
    async deleteNotification(id) {
      appLogger.info("deleteNotification", `[REDUX][${STORES.NOTIFICATIONS}]`)
      this.setAction(globalConst.action.DELETE_NOTIFICATION)
      try {
        if (shouldSkipProtectedDashboardCalls()) {
          this.setData([])
          this.setMessage("msg_notification_deleted")
          this.setAction(globalConst.action.NONE)
          return
        }
        const result = await API.del("prefsApiGateway", `/notifications/${id}`)
        this.setMessage("msg_notification_deleted")
        // Refresh the notifications list after deletion
        await this.fetchNotifications()
        this.setAction(globalConst.action.NONE)
      } catch (error) {
        const _error = error.message || error.response
        this.setError(_error)
        this.setAction(globalConst.action.NONE)
        return
      }
    },

    /**
     *
     * @param {*} id
     */
    async disableOwner(id) {
      // devLog(payload)
      appLogger.info("disableOwner", `[REDUX][${STORES.OWNERS}]`)
      this.setAction(globalConst.action.DISABLE_OWNER)
      try {
        const result = await API.del("prefsApiGateway", `/owners/${id}/status`)
        this.setMessage("msg_owner_disabled")
      } catch (error) {
        const _error = error.message || error.response
        this.setError(_error)
        throw new Error(error)
      }
    },
    /**
     *
     * @param {*} id
     */
    async enableOWner(id) {
      appLogger.info("enableOWner", `[REDUX][${STORES.OWNERS}]`)
      this.setAction(globalConst.action.ENABLE_OWNER)
      try {
        const result = await API.put("prefsApiGateway", `/owners/${id}/status`)
        this.setMessage("msg_owner_enabled")
      } catch (error) {
        const _error = error.message || error.response
        this.setError(_error)
        throw new Error(error)
      }
    },

    /**
     *
     *
     */
    async getPresignedUrl(key) {
      appLogger.info("getPresignedUrl", `[REDUX][${STORES.OWNERS}]`)
      try {
        const url = await API.post("prefsApiGateway", "/owners/upload", {
          key
        })
        
        //   this.setMessage("msg_owner_enabled")
      } catch (error) {
        const _error = error.message || error.response
        this.setError(_error)
        throw new Error(error)
      }
    },

    /**
     * Get owner by PK
     *
     * @param {*} id
     */
    async getOwner(id) {
      appLogger.info("getOwner", `[REDUX][${STORES.OWNERS}]`)
      this.setAction(globalConst.action.GET_OWNER)
      try {
        const result = await API.get("prefsApiGateway", `/owners/${id}`)
        this.setCurrent(result)
        this.setAction(globalConst.action.NONE)
      } catch (error) {
        throw new Error(error)
      }
    },
    /**
     *
     */
    onAcionReset() {
      this.setAction(globalConst.action.NONE)
    },
    /**
     *
     * @param {*} param0
     */
    resetCurrentRecord(payload = {}) {
      this.setCurrent(payload)
    },
    /**
     *
     * @param {*} payload
     */
    onItemPerPageChange(payload) {
      this.setitemPerPage(payload)
      this.setCurrentPage(1)
    },
    /**
     *
     * @param {*} payload
     */
    onCurrentPageChange(payload) {
      this.setCurrentPage(payload)
    },
    /**
     *
     * @param {*} payload
     */
    onFiltersChange(payload) {
      this.setFilters(payload)
    },
    /**
     *
     * @param {*} payload
     */
    onKeywordChange(payload) {
      this.setKeyword(payload)
      this.setCurrentPage(1)
    }
  })
}
